import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Key, PaperPlaneTilt, Flask, CheckCircle, XCircle, FloppyDisk } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";

export default function Manage() {
  const [settings, setSettings] = useState(null);
  const [senderId, setSenderId] = useState("");
  const [useMock, setUseMock] = useState(false);
  const [saving, setSaving] = useState(false);
  const [numbers, setNumbers] = useState([]);
  const [numbersError, setNumbersError] = useState(null);
  const [customFrom, setCustomFrom] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setSettings(data);
      setSenderId(data.sender_id || "");
      setUseMock(data.use_mock);
    });
  }, []);

  useEffect(() => {
    const fetchNumbers = () =>
      api
        .get("/sendivo/numbers")
        .then(({ data }) => {
          setNumbers(data.numbers || []);
          setNumbersError(data.error || null);
        })
        .catch(() => {});
    fetchNumbers();
    window.addEventListener("focus", fetchNumbers);
    return () => window.removeEventListener("focus", fetchNumbers);
  }, []);

  useEffect(() => {
    if (!touched && settings) {
      const inList = numbers.some((n) => n.phone_number === (settings.sender_id || ""));
      setCustomFrom(!!(settings.sender_id && !inList));
    }
  }, [numbers, settings, touched]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { sender_id: senderId, use_mock: useMock });
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <Layout>
        <p className="font-mono text-sm text-sv-muted">LOADING…</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="border-b border-sv-border pb-6">
        <p className="font-mono text-xs text-sv-muted uppercase tracking-widest">// configuration</p>
        <h1 className="font-display font-black text-4xl tracking-tight uppercase mt-1">Manage</h1>
      </div>

      <div className="mt-8 max-w-2xl space-y-4">
        {/* API key status */}
        <div className="border border-sv-border bg-sv-surface p-5">
          <div className="flex items-center gap-2 font-mono text-xs text-sv-muted uppercase tracking-wider">
            <Key size={15} weight="bold" /> SMS Provider
          </div>
          <div className="mt-3 flex items-center gap-2" data-testid="api-key-status">
            {settings.api_key_configured ? (
              <>
                <CheckCircle size={20} weight="fill" className="text-sv-live" />
                <span className="text-sm text-sv-live">SMS provider is connected.</span>
              </>
            ) : (
              <>
                <XCircle size={20} weight="fill" className="text-sv-stop" />
                <span className="text-sm text-sv-secondary">
                  SMS provider is not connected. Contact your administrator to enable sending real texts.
                </span>
              </>
            )}
          </div>
        </div>

        {/* Sender ID */}
        <div className="border border-sv-border bg-sv-surface p-5">
          <label className="flex items-center gap-2 font-mono text-xs text-sv-muted uppercase tracking-wider">
            <PaperPlaneTilt size={15} weight="bold" /> Default Sender ID / From-number
          </label>
          <select
            data-testid="sender-id-select"
            value={customFrom ? "__custom__" : senderId || ""}
            onChange={(e) => {
              const v = e.target.value;
              setTouched(true);
              if (v === "__custom__") {
                setCustomFrom(true);
              } else {
                setCustomFrom(false);
                setSenderId(v);
              }
            }}
            className="mt-3 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
          >
            <option value="">— None (set per campaign) —</option>
            {numbers.map((n) => (
              <option key={n.phone_number} value={n.phone_number}>
                {n.phone_number}
                {n.friendly_name ? ` (${n.friendly_name})` : ""}
                {n.is_default ? " · default" : ""}
                {!n.active ? " · inactive" : ""}
              </option>
            ))}
            <option value="__custom__">Custom / type manually…</option>
          </select>
          {customFrom && (
            <input
              data-testid="sender-id-input"
              value={senderId}
              onChange={(e) => {
                setTouched(true);
                setSenderId(e.target.value);
              }}
              placeholder="e.g. +14155550123 or BRAND"
              className="mt-2 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
            />
          )}
          <p className="mt-2 text-xs text-sv-muted">
            {numbersError
              ? `Couldn't load your numbers (${numbersError}). Choose “Custom” to type one.`
              : numbers.length === 0
              ? "No numbers found in your account yet — add one, or choose “Custom”."
              : "Default used when a campaign has no From set. Add more numbers and they'll appear here."}
          </p>
        </div>

        {/* Mock toggle */}
        <div className="border border-sv-border bg-sv-surface p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 font-mono text-xs text-sv-muted uppercase tracking-wider">
                <Flask size={15} weight="bold" /> Sending mode
              </div>
              <p className="mt-2 text-sm font-mono font-medium" style={{ color: useMock ? "#B45309" : "#16A34A" }}>
                {useMock ? "● MOCK — messages are simulated (not really sent)" : "● LIVE — real SMS are sent"}
              </p>
              <p className="mt-2 text-sm text-sv-secondary max-w-md">
                Turn the toggle <span className="font-semibold">ON for MOCK</span> (fake IDs, ~20% random failures — for
                testing rate-limiting/persistence) or <span className="font-semibold">OFF for LIVE</span> to send real
                texts. Don’t forget to press Save.
              </p>
            </div>
            <button
              data-testid="mock-toggle"
              role="switch"
              aria-checked={useMock}
              onClick={() => setUseMock((v) => !v)}
              className={`relative w-14 h-7 border transition-colors duration-150 shrink-0 ${
                useMock ? "bg-sv-live/20 border-sv-live" : "bg-sv-bg border-sv-border"
              }`}
            >
              <span
                className="absolute top-0.5 h-5 w-5 transition-all duration-150"
                style={{ left: useMock ? "30px" : "2px", background: useMock ? "#16A34A" : "#A1A1AA" }}
              />
            </button>
          </div>
          {!useMock && !settings.api_key_configured && (
            <p className="mt-3 font-mono text-[11px] text-sv-stop">
              WARNING: real mode selected but API key is missing — sends will fail.
            </p>
          )}
        </div>

        <button
          data-testid="save-settings-button"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 h-11 px-6 bg-sv-ink text-sv-onink font-semibold text-sm hover:bg-sv-secondary transition-colors duration-150 disabled:opacity-50"
        >
          <FloppyDisk size={16} weight="bold" />
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </Layout>
  );
}
